import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/calculator',
      name: 'calculator',
      component: () => import('@/pages/calculator.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/layouts/default.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/pages/dashboard.vue'),
        },
        {
          path: 'banking',
          name: 'banking',
          component: () => import('@/pages/banking/index.vue'),
        },
        {
          path: 'mpesa',
          name: 'mpesa',
          component: () => import('@/pages/mpesa/index.vue'),
        },
        {
          path: 'trips',
          name: 'trips',
          component: () => import('@/pages/trips/index.vue'),
        },
        // Ma3Club operator surface
        {
          path: 'ma3club',
          name: 'ma3club-operations',
          component: () => import('@/pages/ma3club/operations.vue'),
          meta: { permission: 'ma3club.booking.view' },
        },
        {
          path: 'ma3club/trip',
          name: 'ma3club-trip',
          component: () => import('@/pages/ma3club/trip.vue'),
          meta: { permission: 'ma3club.booking.view' },
        },
        {
          path: 'ma3club/earnings',
          name: 'ma3club-earnings',
          component: () => import('@/pages/ma3club/earnings.vue'),
          meta: { permission: 'ma3club.settlement.view' },
        },
        {
          path: 'ma3club/schedule',
          name: 'ma3club-schedule',
          component: () => import('@/pages/ma3club/schedule.vue'),
          meta: { permission: 'ma3club.booking.view' },
        },
        // Support access log — the sacco's record of platform support visits.
        // The guard below checks auth only, so the page gates itself on
        // `support.session.view` (SACCO_ADMIN floor) and the sidebar hides it.
        {
          path: 'support-access',
          name: 'support-access',
          component: () => import('@/pages/support-access.vue'),
        },
        // Settings with sub-pages
        {
          path: 'settings',
          name: 'settings',
          redirect: '/settings/assignments',
        },
        {
          path: 'settings/assignments',
          name: 'settings-assignments',
          component: () => import('@/pages/settings/assignments.vue'),
        },
        {
          path: 'settings/vehicles',
          name: 'settings-vehicles',
          component: () => import('@/pages/vehicles/index.vue'),
        },
        {
          path: 'settings/crew',
          name: 'settings-crew',
          component: () => import('@/pages/crew/index.vue'),
        },
        {
          path: 'settings/investors',
          name: 'settings-investors',
          component: () => import('@/pages/settings/investors.vue'),
        },
        {
          path: 'settings/permission-templates',
          name: 'settings-permission-templates',
          component: () => import('@/pages/settings/permission-templates.vue'),
        },
        // Banking Expenses
        {
          path: 'banking-expenses',
          name: 'banking-expenses',
          redirect: '/banking-expenses/daily',
        },
        {
          path: 'banking-expenses/daily',
          name: 'banking-expenses-daily',
          component: () => import('@/pages/banking-expenses/daily.vue'),
        },
        {
          path: 'banking-expenses/weekly',
          name: 'banking-expenses-weekly',
          component: () => import('@/pages/banking-expenses/weekly.vue'),
        },
        {
          path: 'banking-expenses/monthly',
          name: 'banking-expenses-monthly',
          component: () => import('@/pages/banking-expenses/monthly.vue'),
        },
        // Redirect old route
        {
          path: 'settings/expenses',
          redirect: '/banking-expenses/daily',
        },
        // Statements
        {
          path: 'statements/weekly',
          name: 'statements-weekly',
          component: () => import('@/pages/statements/weekly.vue'),
        },
        {
          path: 'statements/provisions',
          name: 'statements-provisions',
          component: () => import('@/pages/statements/provisions.vue'),
        },
        // Redirects for old routes
        {
          path: 'crew',
          redirect: '/settings/crew',
        },
        {
          path: 'vehicles',
          redirect: '/settings/vehicles',
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

// Navigation guard
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
  } else if (to.meta.requiresAuth !== false && authStore.isAuthenticated && !authStore.isAdmin) {
    // Only SACCO_ADMIN and SUPER_ADMIN can access the sacco portal
    authStore.logout()
    next({ name: 'login', query: { error: 'unauthorized' } })
  } else {
    next()
  }
})

export default router
